import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ColorField } from './ColorField';
import { RichTextField } from './RichTextField';
import { Plus, X } from 'lucide-react';

export type TooltipTitleMode = 'ooooi' | 'como-usar' | 'custom';

export interface TooltipItem {
  titleMode?: TooltipTitleMode;
  /** Editable "ooooi" prefix — user can write "oooi", "ooooooi", etc. */
  ooooiPrefix?: string;
  titleWord?: string;
  titleWordBackgroundColor?: string;
  titleWordTextColor?: string;
  /** Free-form title when titleMode === 'custom' */
  titleCustom?: string;
  /** Free-form content (HTML). No automatic bullet formatting. */
  content?: string;
  /** @deprecated Legacy: use content instead. Kept for migration. */
  items?: string[];
  backgroundColor?: string;
  textColor?: string;
}

interface TooltipListInputProps {
  value?: TooltipItem[];
  onChange: (_items: TooltipItem[]) => void;
}

const TITLE_MODE_OPTIONS: { value: TooltipTitleMode; label: string }[] = [
  { value: 'ooooi', label: 'ooooi [marca] 👀' },
  { value: 'como-usar', label: '✨ Como usar ✨' },
  { value: 'custom', label: 'Título personalizado' },
];

const MODE_COLOR_DEFAULTS: Record<TooltipTitleMode, { backgroundColor: string; textColor: string }> = {
  ooooi: { backgroundColor: '#F2FF00', textColor: '#000000' },
  'como-usar': { backgroundColor: '#F5F5F5', textColor: '#000000' },
  custom: { backgroundColor: '#F2FF00', textColor: '#000000' },
};

const defaultTooltipItem = (): TooltipItem => ({
  titleMode: 'ooooi',
  ooooiPrefix: 'ooooi',
  titleWord: '',
  titleWordBackgroundColor: '#ffffff',
  titleWordTextColor: '#000000',
  content: '',
  backgroundColor: '#F2FF00',
  textColor: '#000000',
});

export const TooltipListInput: React.FC<TooltipListInputProps> = ({
  value = [],
  onChange,
}) => {
  const [items, setItems] = useState<TooltipItem[]>(value.length > 0 ? value : []);

  useEffect(() => {
    setItems(value.length > 0 ? value : []);
  }, [value]);

  const addItem = useCallback(() => {
    const updated = [...items, defaultTooltipItem()];
    setItems(updated);
    onChange(updated);
  }, [items, onChange]);

  const removeItem = useCallback(
    (index: number) => {
      const updated = items.filter((_, i) => i !== index);
      setItems(updated);
      onChange(updated);
    },
    [items, onChange]
  );

  const updateItem = useCallback(
    <K extends keyof TooltipItem>(index: number, field: K, fieldValue: TooltipItem[K]) => {
      const updated = items.map((item, i) =>
        i === index ? { ...item, [field]: fieldValue } : item
      );
      setItems(updated);
      onChange(updated);
    },
    [items, onChange]
  );

  const handleTitleModeChange = useCallback(
    (index: number, newMode: TooltipTitleMode) => {
      const updated = items.map((item, i) =>
        i === index ? { ...item, titleMode: newMode, ...MODE_COLOR_DEFAULTS[newMode] } : item
      );
      setItems(updated);
      onChange(updated);
    },
    [items, onChange]
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={addItem} className="gap-2">
          <Plus className="w-4 h-4" />
          Adicionar balão direcional
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Nenhum balão direcional adicionado
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item, index) => {
            const titleMode = item.titleMode ?? 'ooooi';
            return (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">
                    Balão direcional {index + 1}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de balão
                  </label>
                  <select
                    value={titleMode}
                    onChange={(e) => handleTitleModeChange(index, e.target.value as TooltipTitleMode)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                  >
                    {TITLE_MODE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {titleMode === 'ooooi' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prefixo
                        </label>
                        <Input
                          placeholder="ooooi"
                          value={item.ooooiPrefix ?? 'ooooi'}
                          onChange={(e) => updateItem(index, 'ooooiPrefix', e.target.value)}
                          className="bg-white font-['Times_New_Roman']"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Marca / área em destaque
                        </label>
                        <Input
                          placeholder="Ex: @todos, área xxx, Amstel"
                          value={item.titleWord ?? ''}
                          onChange={(e) => updateItem(index, 'titleWord', e.target.value)}
                          className="bg-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <ColorField
                        label="Cor de fundo da palavra"
                        value={item.titleWordBackgroundColor ?? '#ffffff'}
                        onChange={(v) => updateItem(index, 'titleWordBackgroundColor', v)}
                      />
                      <ColorField
                        label="Cor do texto da palavra"
                        value={item.titleWordTextColor ?? '#000000'}
                        onChange={(v) => updateItem(index, 'titleWordTextColor', v)}
                      />
                    </div>
                  </>
                )}

                {titleMode === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Título personalizado
                    </label>
                    <Input
                      placeholder="Ex: Na prática, Inspiração..."
                      value={item.titleCustom ?? ''}
                      onChange={(e) => updateItem(index, 'titleCustom', e.target.value)}
                      className="bg-white"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <ColorField
                    label="Cor de fundo do balão"
                    value={item.backgroundColor ?? MODE_COLOR_DEFAULTS[titleMode].backgroundColor}
                    onChange={(v) => updateItem(index, 'backgroundColor', v)}
                  />
                  <ColorField
                    label="Cor do texto do balão"
                    value={item.textColor ?? '#000000'}
                    onChange={(v) => updateItem(index, 'textColor', v)}
                  />
                </div>
                <RichTextField
                  label="Conteúdo do balão"
                  value={item.content ?? ''}
                  onChange={(value) => updateItem(index, 'content', value)}
                  placeholder="Texto livre. Use parágrafos ou tópicos conforme preferir."
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
